
import { useMemo } from "react";
import { prepareAverageData, calculateYAxisDomain } from "../GrowthDataUtils";

interface GrowthChartDataProps {
  chartData: any[];
  displayOriginalValue: number | null;
  showAverageLine: boolean;
  getAverageValueForAge: (ageMonths: number) => number;
  valueKey: string;
  babyAgeInMonths: number;
}

// Use a module-level cache for expensive calculations
const dataPointsCache = new Map<string, any[]>();

export function useGrowthChartData({
  chartData,
  displayOriginalValue,
  showAverageLine,
  getAverageValueForAge,
  valueKey,
  babyAgeInMonths
}: GrowthChartDataProps) {
  // Generate cache key for allDataPoints
  const dataPointsCacheKey = `${valueKey}-${displayOriginalValue}-${chartData.length}`;
  
  // Memoize all data points calculation to prevent recalculation on every render
  const allDataPoints = useMemo(() => {
    // Check cache first
    if (dataPointsCache.has(dataPointsCacheKey)) {
      return dataPointsCache.get(dataPointsCacheKey) || [];
    }
    
    let dataPoints = [];
    
    if (displayOriginalValue !== null) {
      dataPoints.push({
        [valueKey]: displayOriginalValue,
        formattedDate: "Birth"
      });
    }
    
    const result = [...dataPoints, ...chartData];
    
    // Cache the result
    dataPointsCache.set(dataPointsCacheKey, result);
    
    return result;
  }, [chartData, displayOriginalValue, valueKey, dataPointsCacheKey]);
  
  // Memoize average data calculation
  const preparedAverageData = useMemo(() => {
    return prepareAverageData(
      allDataPoints,
      showAverageLine,
      getAverageValueForAge,
      valueKey,
      babyAgeInMonths
    );
  }, [allDataPoints, showAverageLine, getAverageValueForAge, valueKey, babyAgeInMonths]);
  
  // Memoize y-axis domain calculation
  const yAxisDomain = useMemo(() => {
    return calculateYAxisDomain(
      allDataPoints,
      preparedAverageData,
      showAverageLine,
      valueKey
    );
  }, [allDataPoints, preparedAverageData, showAverageLine, valueKey]);

  return {
    allDataPoints,
    preparedAverageData,
    yAxisDomain
  };
}
